<script setup lang="ts">
import { onBeforeMount, ref, onMounted, watch } from "vue";
import { environmentData } from "./helper/environment";
import { useGlobalStore } from "./store/global";
import siteCoreMockData from "./mock-data/siteCoreData.json";
import { initializeIamViaIgn } from "@/utils/authentication-helpers";
import { setSessionUser, deviceInfo } from "@/utils/analytics";
import { checkDeviceType } from "@/utils/common";
import { decodeJwtBody } from "@maersk-global/shared-js";
import { useLDReady, useLDFlag } from "launchdarkly-vue-client-sdk";

const terminalScopesFlag = useLDFlag("apmt-tnt-terminals", {});

const globalStore = useGlobalStore();
const isScLoaded = ref(false);
const ldReady = useLDReady();

onBeforeMount(async () => {
  localStorage.setItem("terminal", "Global");
  localStorage.setItem("analytics_appName", "track-and-trace");
  document.addEventListener("IGN_Init", () => {
    initializeIamViaIgn();
  });
  const headers = new Headers();
  headers.append("Accept", "application/json");
  headers.append("Consumer-Key", environmentData.siteCoreApiConsumerKey);

  const fetchErrorPhrases = async () => {
    try {
      const url = environmentData.siteCoreApiUrl.replace("{lang}", localStorage.getItem("language") || "en");
      const response = await fetch(url, {
        method: "GET",
        headers: headers
      });

      if (!response.ok || response.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const jsonResponse = await response.json();
      globalStore.updateSitecoreData(jsonResponse);
      isScLoaded.value = true;
    } catch (error) {
      isScLoaded.value = true;
      globalStore.updateSitecoreData(siteCoreMockData); // Fallback to hardcoded data if fetch fails
    }
  };
  await fetchErrorPhrases();
});

onMounted(() => {
  localStorage.setItem("terminal", "Global");
  localStorage.setItem("analytics_appName", "track-and-trace");
  const deviceType = checkDeviceType() || "";
  deviceInfo(deviceType);
  updateUserData();
  const lang = localStorage.getItem("language");
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  const mdsElement = document.querySelector(".mds");
  if (mdsElement) {
    mdsElement.classList.toggle("rtl", lang === "ar");
  }
});

const updateUserData = () => {
  const decodedToken = decodeJwtBody(localStorage.getItem(environmentData.accessTokenLink));
  if (decodedToken) {
    const userData = {
      email: decodedToken?.sub,
      id: decodedToken?.userUuid,
      username: decodedToken?.subname,
      attributes: {
        scopes: JSON.stringify(decodedToken?.scope)
      }
    };
    globalStore.updateUserEmail(userData.email);
    setSessionUser(userData);
  }
};

watch(
  terminalScopesFlag,
  async (newVal: any) => {
    globalStore.updateAllowedTerminals(newVal?.terminals || []);
  },
  { immediate: true }
);
</script>

<template>
  <!-- Router configuration for the main widget -->
  <div v-if="isScLoaded && ldReady">
    <router-view />
  </div>
</template>

<style>
[dir="rtl"] div {
  direction: rtl;
  text-align: right;
}
.mds.rtl * {
  direction: rtl;
  text-align: right;
}
</style>
